/**
 * #sidebar-tree fixture: the demo tree and lookups. Data only.
 */

export type TreeNode = {
  id: string
  name: string
  kind: 'folder' | 'file'
  /** frontmatter kind when the file name does not carry it (soul, skill). */
  docKind?: string
  /** Short markdown shown on the grid tile. */
  preview?: string
  /** Picture shown on the tile when the file is an image. */
  image?: string
  /** Body line count for the grid badge. Images omit it. */
  lines?: number
  /** Short relative label, already phrased: "Edited 2d ago". */
  edited?: string
  children?: TreeNode[]
}

export function svgPhoto(background: string, shapes: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="400" viewBox="0 0 640 400"><rect width="640" height="400" fill="${background}"/>${shapes}</svg>`
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`
}

export const COVER_IMAGE = svgPhoto(
  '#1e3a5f',
  '<circle cx="480" cy="90" r="42" fill="#f6c453"/><path d="M0 280 L140 190 L260 250 L400 150 L640 260 L640 400 L0 400 Z" fill="#2f6b4f"/><path d="M0 320 L180 240 L320 300 L520 210 L640 300 L640 400 L0 400 Z" fill="#16382a"/>',
)

export const HARBOR_IMAGE = svgPhoto(
  '#7eb6d9',
  '<rect y="230" width="640" height="170" fill="#2c6f91"/><rect x="250" y="150" width="16" height="120" fill="#5c3b2e"/><path d="M258 160 L360 210 L258 210 Z" fill="#f4efe6"/><circle cx="120" cy="80" r="28" fill="#fff6d8"/>',
)

export const ROOT_ID = 'all'
export const MIXED_ID = 'rowan'

export const TREE: TreeNode[] = [
  {
    id: ROOT_ID,
    name: 'All files',
    kind: 'folder',
    children: [
      {
        id: 'photos',
        name: 'Photos',
        kind: 'folder',
        children: [
          { id: 'cover', name: 'Cover.png', kind: 'file', docKind: 'image', image: COVER_IMAGE },
          { id: 'harbor', name: 'Harbor.jpg', kind: 'file', docKind: 'image', image: HARBOR_IMAGE },
        ],
      },
      {
        id: 'soul',
        name: '.soul',
        kind: 'folder',
        children: [
          {
            id: 'identity',
            name: 'identity.md',
            kind: 'file',
            docKind: 'soul',
            preview: '## Identity\n\nWe write things down so the next person can **pick up the work**.',
          },
          {
            id: 'values',
            name: 'values.md',
            kind: 'file',
            docKind: 'soul',
            preview: '## Values\n\n- Say the quiet part\n- Leave the place clearer',
          },
        ],
      },
      {
        id: 'skills',
        name: '.skills',
        kind: 'folder',
        children: [
          {
            id: 'explore-soul',
            name: 'explore-soul',
            kind: 'folder',
            children: [
              {
                id: 'skill-md',
                name: 'SKILL.md',
                kind: 'file',
                docKind: 'skill',
                preview: '## Explore soul\n\nShow which context tiers have content for this folder.',
              },
            ],
          },
        ],
      },
      {
        id: 'deck',
        name: 'Deck',
        kind: 'folder',
        children: [
          {
            id: 'board',
            name: 'Board.md',
            kind: 'file',
            preview: '## Board\n\nOpen items for the week.\n\n- Cover art\n- Manual review',
          },
        ],
      },
      {
        id: 'users',
        name: 'users',
        kind: 'folder',
        children: [
          {
            id: MIXED_ID,
            name: 'rowan',
            kind: 'folder',
            children: [
              {
                id: 'rowan-todos',
                name: 'todos',
                kind: 'folder',
                children: [
                  { id: 'rowan-todo-staff', name: 'Staff meeting.md', kind: 'file', preview: '## Staff meeting\n\nAgenda for Monday.' },
                  { id: 'rowan-todo-time', name: 'Timesheets.md', kind: 'file', preview: '## Timesheets\n\nHours for the week.' },
                ],
              },
              {
                id: 'rowan-meetings',
                name: 'meetings',
                kind: 'folder',
                children: [
                  {
                    id: 'rowan-meetings-soul',
                    name: '.soul',
                    kind: 'folder',
                    children: [
                      {
                        id: 'rowan-meetings-identity',
                        name: 'identity.md',
                        kind: 'file',
                        docKind: 'soul',
                        preview: '## Identity\n\nHow this folder should be read.',
                      },
                    ],
                  },
                  { id: 'rowan-meetings-notes', name: 'Notes.md', kind: 'file', preview: '## Notes\n\nLast sync with the client.' },
                ],
              },
              { id: 'rowan-archive', name: 'archive', kind: 'folder', children: [] },
              {
                id: 'rowan-priorities',
                name: 'Weekly Priorities.md',
                kind: 'file',
                lines: 42,
                edited: 'Edited 2d ago',
                preview: '## Weekly Priorities\n\n- Staff meeting\n- Timesheets\n- Hub page',
              },
              {
                id: 'rowan-scrum',
                name: 'Daily Scrum.md',
                kind: 'file',
                lines: 18,
                edited: 'Edited 5h ago',
                preview: '## Daily Scrum\n\nGGIA and the hub page.',
              },
            ],
          },
        ],
      },
      {
        id: 'readme',
        name: 'Readme.md',
        kind: 'file',
        preview: '## Readme\n\nStart with **Photos**, then the manual.\n\n- Images show on the card\n- Other types keep their icon',
      },
      { id: 'manual', name: 'Manual.pdf', kind: 'file', docKind: 'pdf' },
      { id: 'budget', name: 'Budget.csv', kind: 'file', docKind: 'csv' },
      { id: 'talk', name: 'Talk.mp3', kind: 'file', docKind: 'audio' },
      { id: 'page', name: 'Page.html', kind: 'file', docKind: 'html' },
    ],
  },
]

export function findNode(id: string, nodes: TreeNode[] = TREE): TreeNode | null {
  for (const node of nodes) {
    if (node.id === id) return node
    if (node.children) {
      const hit = findNode(id, node.children)
      if (hit) return hit
    }
  }
  return null
}

export function parentId(id: string, nodes: TreeNode[] = TREE, parent: string | null = null): string | null {
  for (const node of nodes) {
    if (node.id === id) return parent
    if (node.children) {
      const hit = parentId(id, node.children, node.id)
      if (hit) return hit
    }
  }
  return null
}
